<script lang="ts" setup>
import { parse, format } from 'date-fns'
import {
  MediaImageFragment,
  ToccoCourseFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoModule,
  ToccoModuleFieldsFragment,
  ToccoModuleNextDatesDocument,
  ToccoModuleNextDatesQueryVariables,
  ToccoSeminar,
  ToccoUser,
  ToccoWebinar,
  ToccoWebinarFieldsFragment,
} from '~/api/graphql/generated'
import { Link } from '~/types'
import { Employee } from '../components/EmployeeSingle.vue'
import { ToccoItem } from '~/api/drupal'
import { useTypedAsyncQuery } from '~/lib/utils/graphqlDocument'

const props = defineProps<{
  entity:
    | ToccoWebinarFieldsFragment
    | ToccoCourseFieldsFragment
    | ToccoModuleFieldsFragment
    | ToccoIntroModuleFieldsFragment
    | ToccoELearningFieldsFragment
  breadcrumbs: Link[]
}>()

const { t } = useI18n()

const computedEntity = computed(() => props.entity)
const fieldToccoDataJSON = computed(() => JSON.parse(computedEntity.value.fieldToccoData ?? ''))

const description = computed(
  () => `<span></span>${fieldToccoDataJSON.value.paths.description?.value.split('\n').join('<br/>')}`, // the span is needed to prevent the text from being cut off by the TextSpecial component
)
const subtitle = computed(() => fieldToccoDataJSON.value.paths.subtitle?.value.split('\n').join('<br/>'))

const currentModuleId = computed(() => (computedEntity.value.id || 0).toString())
const toccoModuleNextDatesVariables: ToccoModuleNextDatesQueryVariables = {
  abbreviation: computedEntity.value.fieldToccoAbbreviationBase!,
  currentModuleId: currentModuleId.value,
  after: '2000-01-01',
}
const { data } = await useTypedAsyncQuery(ToccoModuleNextDatesDocument, toccoModuleNextDatesVariables)

const nextDatesItems = computed(() =>
  data.value?.toccoQuery?.entities?.map((module) => {
    const toccoModule = module as ToccoModule | ToccoWebinar | ToccoCourseFieldsFragment
    return {
      id: toccoModule.id?.toString() ?? '',
      type: toccoModule.__typename || '',
      title: toccoModule.title,
      teaser: toccoModule.fieldTeaser ?? '',
      date: toccoModule.fieldStartDate?.value
        ? format(parse(toccoModule.fieldStartDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
        : null,
      link: { url: toccoModule.entityUrl?.path },
    } as ToccoItem
  }),
)

const associatedSeminarItems = computed(() => {
  const seminars = [
    ...(computedEntity.value.reverseFieldRequiredModulesTocco.entities ?? []),
    ...(computedEntity.value.reverseFieldOptionalModulesTocco.entities ?? []),
  ]
  return seminars.filter(Boolean).map((seminar) => {
    const toccoSeminar = seminar as ToccoSeminar
    return {
      id: toccoSeminar.id?.toString() ?? '',
      type: seminar?.__typename || '',
      title: toccoSeminar.title,
      teaser: toccoSeminar.fieldTeaser ?? '',
      date: '',
      link: { url: toccoSeminar.entityUrl?.path },
    } as ToccoItem
  })
})

const processUser = (user: ToccoUser, isLecturer = false): Employee => {
  const academicTitle = user?.fieldAcademicTitle ? `${user.fieldAcademicTitle} ` : ''
  const firstName = user?.fieldFirstName ? `${user.fieldFirstName} ` : ''
  const lastName = user?.fieldLastName ? `${user.fieldLastName} ` : ''
  return {
    image: (user?.fieldProfilePicture?.entity as MediaImageFragment) || {
      fieldMediaImage: { url: '/tocco-user-placeholder.png', height: 135, width: 135 },
    },
    name: `${academicTitle}${firstName}${lastName}`.trim(),
    position: (isLecturer ? user?.fieldLecturerFunction : user?.fieldJob) ?? '',
  }
}

const lecturers = computed(() => {
  // Get lecturers and sort them by last name
  const lecturersList = computedEntity.value.fieldLecturers || []
  return lecturersList
    .map((userData) => {
      const user = userData?.entity as ToccoUser
      return {
        lastName: user?.fieldLastName?.toLowerCase() || '',
        processed: processUser(user, true),
      }
    })
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
    .map((item) => item.processed)
})

const responsibles = computed(() => [processUser(computedEntity.value.fieldResponsible?.entity as ToccoUser)])
</script>

<template>
  <SectionHero :entity="entity" :breadcrumbs="breadcrumbs" type="petrol" />
  <SectionIntrotext v-if="subtitle" :text="subtitle" />
  <ToccoBaseSidebar :entity="entity" />
  <SectionText v-if="description" :text="description" />
  <SectionToccoDates :entity="entity" data-id="dates" :show-next-dates-link="!!nextDatesItems?.length" />
  <SectionToccoAccordion :entity="entity" />
  <SectionToccoShare />
  <SectionToccoEntityTeaser
    v-if="associatedSeminarItems?.length"
    :title="t('tocco.detail.associatedSeminars')"
    :items="associatedSeminarItems"
  />
  <SectionToccoEntityTeaser
    v-if="nextDatesItems?.length"
    :title="t('tocco.detail.nextDates')"
    :items="nextDatesItems"
    data-id="next-modules"
  />
  <SectionToccoContactPersons
    v-if="lecturers?.length"
    :title="t('tocco.detail.lecturersTitle')"
    :persons="lecturers"
    :hide-images="true"
    data-id="lecturers"
  />
  <SectionToccoContactPersons
    v-if="responsibles?.length"
    :title="t('tocco.detail.responsiblesTitle')"
    :persons="responsibles"
    data-id="responsibles"
  />
</template>

<style lang="scss" scoped></style>
